import React, { useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Flame, Star, TrendingUp, MessageSquare, Users, Settings, Sparkles, Plus } from "lucide-react";
import { toast } from "sonner";

export default function ParentDashboard() {
  const [children, setChildren] = useState([]);
  const [selected, setSelected] = useState(null);
  const [progress, setProgress] = useState([]);
  const [summary, setSummary] = useState("");
  const [stats, setStats] = useState({ completed: 0, avg_score: 0 });
  const [linkEmail, setLinkEmail] = useState("");
  const [loadingAI, setLoadingAI] = useState(false);

  const loadChildren = async () => {
    const { data } = await api.get("/parent/children");
    setChildren(data);
    if (data.length > 0) {
      const stillThere = selected && data.find(c => c.id === selected.id);
      setSelected(stillThere || data[0]);
    } else {
      setSelected(null);
    }
  };
  useEffect(() => { loadChildren(); }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/progress/user/${selected.id}`).then(r => setProgress(r.data));
  }, [selected]);

  const generateSummary = async () => {
    if (!selected) return;
    setLoadingAI(true);
    try {
      const { data } = await api.post("/ai/parent-summary", { child_id: selected.id });
      setSummary(data.summary);
      setStats(data.stats);
    } catch { toast.error("AI summary failed"); }
    finally { setLoadingAI(false); }
  };

  const linkChild = async () => {
    try {
      await api.post("/parent/link-child", { child_email: linkEmail });
      setLinkEmail("");
      await loadChildren();
      toast.success("Child linked!");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar variant="parent"/>
      <main className="max-w-7xl mx-auto p-4 sm:p-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="font-display text-3xl font-bold text-slate-800">Parent Dashboard</h1>
          <div className="flex gap-2">
            <input value={linkEmail} onChange={e=>setLinkEmail(e.target.value)} placeholder="child@email.com"
              data-testid="link-child-input"
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm"/>
            <button onClick={linkChild} data-testid="link-child-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-4 py-2 text-sm font-semibold inline-flex items-center gap-1">
              <Plus className="w-4 h-4"/> Link child
            </button>
          </div>
        </div>

        {children.length === 0 && (
          <div className="parent-card p-8 text-center">
            <Users className="w-12 h-12 text-slate-400 mx-auto"/>
            <p className="text-slate-600 mt-2">No children linked yet. Have your child register, then add their email above.</p>
          </div>
        )}

        {children.length > 0 && selected && (
          <>
            <div className="flex gap-2 mb-6 flex-wrap">
              {children.map(c => (
                <button key={c.id} onClick={()=>setSelected(c)}
                  data-testid={`child-tab-${c.id}`}
                  className={`px-4 py-2 rounded-xl font-semibold text-sm ${selected.id===c.id?"bg-indigo-600 text-white":"bg-white border border-slate-200 text-slate-700"}`}>
                  {c.name}
                </button>
              ))}
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Stat icon={Star} label="XP" value={selected.xp} color="amber"/>
              <Stat icon={TrendingUp} label="Level" value={selected.level} color="indigo"/>
              <Stat icon={Flame} label="Streak" value={selected.streak} color="rose"/>
              <Stat icon={Sparkles} label="Completed" value={stats.completed} color="emerald"/>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              <div className="parent-card p-6 lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-xl font-bold text-slate-800">AI Daily Recap</h2>
                  <button onClick={generateSummary} disabled={loadingAI} data-testid="gen-summary-btn"
                    className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-3 py-1.5 font-semibold">
                    {loadingAI ? "Thinking..." : "Generate"}
                  </button>
                </div>
                <p className="text-slate-700 leading-relaxed" data-testid="ai-summary">
                  {summary || "Click Generate to get an AI-powered insight into your child's progress today."}
                </p>
              </div>

              <div className="parent-card p-6">
                <h2 className="font-display text-xl font-bold text-slate-800 mb-3">Quick Links</h2>
                <div className="space-y-2">
                  <Link to="/parent/messages" className="flex items-center gap-2 text-indigo-600 hover:underline" data-testid="link-messages">
                    <MessageSquare className="w-4 h-4"/> Messages
                  </Link>
                  <Link to="/shared" className="flex items-center gap-2 text-indigo-600 hover:underline" data-testid="link-shared-from-parent">
                    <Users className="w-4 h-4"/> Shared tasks
                  </Link>
                  <Link to="/parent/settings" className="flex items-center gap-2 text-indigo-600 hover:underline" data-testid="link-settings">
                    <Settings className="w-4 h-4"/> Settings
                  </Link>
                </div>
              </div>
            </div>

            <div className="parent-card p-6 mt-6">
              <h2 className="font-display text-xl font-bold text-slate-800 mb-3">Recent activity</h2>
              {progress.length === 0 && <p className="text-slate-500 text-sm">No activity yet.</p>}
              <ul className="divide-y">
                {progress.slice(0, 8).map(p => (
                  <li key={p.id} className="py-2 flex justify-between text-sm">
                    <span className="text-slate-700">{p.status === "completed" ? "Completed" : "Tried"} a challenge</span>
                    <span className="text-slate-500">Score {p.score} · {new Date(p.timestamp).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="parent-card p-5 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl bg-${color}-100 text-${color}-600 flex items-center justify-center`}>
        <Icon className="w-5 h-5"/>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
        <p className="font-display text-2xl font-bold text-slate-800">{value}</p>
      </div>
    </div>
  );
}
